/**
 * The Calendar view's sidebar (activity bar → Calendar; Google Calendar's left column; design board "Main"): Create ▾
 * (Event / Booking page), the mini month (picks the shown day), My calendars, and Booking pages — each row with its
 * colour, "30 min · Bundle meeting" (or "Paused · …") and a ⋯ menu: Open booking page, Copy link, Edit, Pause /
 * Resume, Reset link, Delete (Reset and Delete confirm; booked events stay). Works without Mail.
 */
import type { BookingPage } from "@bundle/contracts";
import { Shell, type OpenTarget } from "@bundle/core";
import * as Booking from "@bundle/core/logic/booking";
import * as Cal from "@bundle/core/logic/calendar";
import type { ColorToken } from "@bundle/tokens";
import {
  Button,
  DatePicker,
  Icon,
  IconButton,
  ListRow,
  Menu,
  Sheet,
  Stack,
  Text,
  openLink,
  useCalendar,
  useFeatureAccess,
  useLocale,
  useT,
  useTr,
  useViewModel,
  useWorkspace,
  type CalendarServices,
  type MenuEntry,
} from "@bundle/ui";
import { useEffect, useState } from "react";
import { requestNewEvent } from "../screens/calendar/intents.ts";

type Props = { activeUrl: string; open: (url: string, target?: OpenTarget) => void };

export function SidebarCalendar({ activeUrl, open }: Props) {
  const t = useT();
  const cal = useCalendar();
  const access = useFeatureAccess();
  const ws = useWorkspace();
  const url = Shell.formatUrl({ type: "calendar", ws: ws.slug });
  const inCalendar = Shell.parseUrl(activeUrl)?.type === "calendar";
  if (cal === undefined) return null;
  const canChange = access("calendar") === "full";
  const create: MenuEntry[] = [
    {
      id: "event",
      label: t("booking.create.event"),
      icon: "calendar",
      onSelect: () => {
        cal.booking.closeEditor();
        if (!inCalendar) open(url);
        requestNewEvent();
      },
    },
    {
      id: "booking",
      label: t("booking.create.page"),
      description: t("booking.create.pageHelp"),
      icon: "booking",
      onSelect: () => {
        if (!inCalendar) open(url);
        cal.booking.edit("new");
      },
    },
  ];
  return (
    <>
      <div className="sidebar-view-header">
        <Text variant="headline" weight="semibold">
          {t("shell.view.calendar")}
        </Text>
      </div>
      {canChange ? (
        <div className="sidebar-view-note">
          <Menu items={create} testID="sidebar-calendar-create-menu">
            <Button variant="secondary" icon="plus" label={t("booking.create.button")} onPress={() => undefined} testID="sidebar-calendar-create" />
          </Menu>
        </div>
      ) : null}
      <MiniMonth services={cal} onPick={() => (inCalendar ? undefined : open(url))} />
      <MyCalendars services={cal} onOpen={() => open(url)} />
      <BookingPages services={cal} canChange={canChange} onOpenCalendar={() => (inCalendar ? undefined : open(url))} />
    </>
  );
}

/** Google's mini month: picking a day shows it in the grid (the current view's span around it). */
function MiniMonth({ services, onPick }: { services: CalendarServices; onPick: () => void }) {
  const locale = useLocale();
  const s = useViewModel(services.calendar);
  const p = Cal.localParts(s.anchor, s.timeZone);
  const value = `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
  return (
    <div className="sidebar-minimonth" data-testid="sidebar-calendar-month">
      <DatePicker
        value={value}
        locale={locale === "pt-BR" ? "pt-BR" : "en"}
        weekStartsOn={s.firstDay}
        onChange={(date) => {
          const [y, m, d] = date.split("-").map(Number);
          services.calendar.goTo(Cal.fromLocal(y ?? p.year, m ?? p.month, d ?? p.day, 0, 0, s.timeZone), s.mode);
          onPick();
        }}
        testID="sidebar-calendar-datepicker"
      />
    </div>
  );
}

/**
 * "My calendars", mine first (named after me), then my other calendars ("from Google": secondary Google calendars
 * imported with my events), and a note while Google Calendar still syncs in.
 */
function MyCalendars({ services, onOpen }: { services: CalendarServices; onOpen: () => void }) {
  const t = useT();
  const ws = useWorkspace();
  const s = useViewModel(services.calendar);
  const me = ws.state.cache?.members[ws.state.meId]?.displayName ?? t("nav.calendar");
  return (
    <div className="sidebar-group" data-testid="sidebar-calendars">
      <div className="sidebar-view-label">
        <Text variant="caption" weight="semibold" color="text.tertiary">
          {t("calendar.mine")}
        </Text>
      </div>
      <ListRow title={me} leading={<Icon name="calendar" size="icon.sm" color="accent.text" />} onPress={onOpen} testID="sidebar-calendar-main" />
      {s.calendars.map((c) => (
        <ListRow
          key={c.id}
          title={c.name}
          leading={<Icon name="calendar" size="icon.sm" color={c.color === undefined ? "text.secondary" : (`tag.${c.color}.fg` as ColorToken)} />}
          trailing={
            c.source === "google" ? (
              <Text variant="caption" color="text.tertiary">
                {t("calendar.fromGoogle")}
              </Text>
            ) : undefined
          }
          onPress={onOpen}
          testID={`sidebar-calendar-${c.id}`}
        />
      ))}
      {s.googleSync ? (
        <div className="sidebar-view-note" data-testid="sidebar-calendar-google-sync">
          <Text variant="footnote" color="text.secondary">
            {t("calendar.googleSync")}
          </Text>
        </div>
      ) : null}
    </div>
  );
}

function BookingPages({ services, canChange, onOpenCalendar }: { services: CalendarServices; canChange: boolean; onOpenCalendar: () => void }) {
  const t = useT();
  const tr = useTr();
  const b = useViewModel(services.booking);
  const [confirm, setConfirm] = useState<{ kind: "reset" | "delete"; page: BookingPage } | undefined>(undefined);
  const [copied, setCopied] = useState<string | undefined>(undefined);
  useEffect(() => {
    void services.booking.load();
  }, [services]);
  useEffect(() => {
    if (copied === undefined) return;
    const h = setTimeout(() => setCopied(undefined), 2000);
    return () => clearTimeout(h);
  }, [copied]);
  const copy = (p: BookingPage) => void navigator.clipboard?.writeText(p.url).then(() => setCopied(p.id));
  const edit = (p: BookingPage) => {
    onOpenCalendar();
    services.booking.edit(p.id);
  };
  const menu = (p: BookingPage): MenuEntry[] => [
    { id: "open", label: t("booking.menu.open"), icon: "link", onSelect: () => openLink(p.url) },
    { id: "copy", label: t("booking.copyLink"), icon: "copy", onSelect: () => copy(p) },
    ...(canChange
      ? ([
          { id: "edit", label: t("booking.menu.edit"), icon: "edit", onSelect: () => edit(p) },
          { id: "pause", label: p.paused ? t("booking.menu.resume") : t("booking.menu.pause"), icon: p.paused ? "play" : "pause", onSelect: () => void services.booking.setPaused(p.id, !p.paused) },
          { id: "sep", separator: true },
          { id: "reset", label: t("booking.menu.reset"), icon: "reload", onSelect: () => setConfirm({ kind: "reset", page: p }) },
          { id: "delete", label: t("booking.menu.delete"), icon: "trash", destructive: true, onSelect: () => setConfirm({ kind: "delete", page: p }) },
        ] as MenuEntry[])
      : []),
  ];
  const subtitle = (p: BookingPage) => {
    const sum = Booking.bookingPageSummary(p);
    const parts = [sum.duration, tr(Booking.locationLabel(sum.location))];
    return (sum.paused ? [t("booking.paused"), ...parts] : parts).join(" · ");
  };
  return (
    <div className="sidebar-group" data-testid="sidebar-booking-pages">
      <div className="sidebar-view-label">
        <Text variant="caption" weight="semibold" color="text.tertiary">
          {t("booking.pages")}
        </Text>
        {canChange ? (
          <IconButton
            icon="plus"
            size="sm"
            accessibilityLabel={t("booking.create.page")}
            onPress={() => {
              onOpenCalendar();
              services.booking.edit("new");
            }}
            testID="sidebar-booking-new"
          />
        ) : null}
      </div>
      {b.status === "ready" && b.pages.length === 0 ? (
        <div className="sidebar-view-note" data-testid="sidebar-booking-empty">
          <Stack gap="1">
            <Text variant="footnote" weight="semibold">
              {t("booking.empty.title")}
            </Text>
            <Text variant="footnote" color="text.secondary">
              {t("booking.empty.body")}
            </Text>
          </Stack>
        </div>
      ) : null}
      {b.pages.map((p) => (
        <ListRow
          key={p.id}
          title={p.title}
          subtitle={copied === p.id ? t("booking.linkCopied") : subtitle(p)}
          density="regular"
          muted={p.paused}
          current={b.editing === p.id}
          leading={<Icon name="booking" size="icon.sm" color={p.paused ? "text.tertiary" : p.color === undefined ? "text.link" : (`tag.${p.color}.fg` as ColorToken)} />}
          actions={
            <>
              <IconButton icon="copy" size="sm" accessibilityLabel={t("booking.copyLinkTo", { title: p.title })} onPress={() => copy(p)} testID={`sidebar-booking-${p.id}-copy`} />
              <Menu items={menu(p)} align="end" testID={`sidebar-booking-${p.id}-menu`}>
                <IconButton icon="more" size="sm" accessibilityLabel={t("booking.moreFor", { title: p.title })} onPress={() => undefined} testID={`sidebar-booking-${p.id}-more`} />
              </Menu>
            </>
          }
          contextMenu={menu(p)}
          onPress={() => (canChange ? edit(p) : openLink(p.url))}
          testID={`sidebar-booking-${p.id}`}
        />
      ))}
      <Sheet
        open={confirm !== undefined}
        onClose={() => setConfirm(undefined)}
        title={confirm === undefined ? undefined : confirm.kind === "reset" ? t("booking.reset.title") : t("booking.delete.title", { title: confirm.page.title })}
        testID="sidebar-booking-confirm"
      >
        <Stack gap="4" padding="4">
          <Text color="text.secondary">{confirm?.kind === "reset" ? t("booking.reset.body") : t("booking.delete.body")}</Text>
          <Stack direction="horizontal" gap="2" justify="end">
            <Button variant="secondary" label={t("common.cancel")} onPress={() => setConfirm(undefined)} testID="sidebar-booking-confirm-cancel" />
            <Button
              variant={confirm?.kind === "delete" ? "destructive" : "primary"}
              label={confirm?.kind === "reset" ? t("booking.menu.resetShort") : t("booking.menu.deleteShort")}
              onPress={() => {
                const c = confirm;
                setConfirm(undefined);
                if (c === undefined) return;
                if (c.kind === "reset") void services.booking.resetLink(c.page.id);
                else void services.booking.remove(c.page.id);
              }}
              testID="sidebar-booking-confirm-ok"
            />
          </Stack>
        </Stack>
      </Sheet>
    </div>
  );
}
